import { createTheme, Theme } from '@mui/material/styles';
import { OrganizationBranding } from '../config/tenant';
import baseTheme from './theme';
import { UI_FONT_STACK } from './fonts';

// Color formats MUI accepts directly (see MUI error #9).
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNC_RE = /^(rgb|rgba|hsl|hsla|color)\(.*\)$/i;

/**
 * Normalize a tenant-supplied brand color into a value MUI's `createTheme`
 * (specifically `augmentColor` → `darken`/`lighten`) can consume.
 *
 * Tenant branding comes from the API / localStorage cache and is not guaranteed
 * to be well-formed. A malformed value (e.g. `"rgb255, 156, 63"` — missing the
 * `rgb(...)` wrapper) makes MUI throw error #9, which trips the app-wide error
 * boundary and white-screens every route. This guards against that by:
 *   1. passing through already-valid hex / rgb()/rgba()/hsl()/hsla()/color() values,
 *   2. repairing recoverable cases (missing parentheses, bare hex without `#`),
 *   3. otherwise returning a safe `fallback`.
 */
export function sanitizeColor(input: unknown, fallback: string): string {
  if (typeof input !== 'string') return fallback;
  const c = input.trim();
  if (!c) return fallback;

  // Already valid.
  if (HEX_RE.test(c) || FUNC_RE.test(c)) return c;

  // Repair a functional color missing its parentheses, e.g.
  // "rgb255, 156, 63" → "rgb(255, 156, 63)" or "rgba0,0,0,.5" → "rgba(0,0,0,.5)".
  const fn = c.match(/^(rgba?|hsla?|color)\s*\(?\s*([0-9.,%\s/a-z-]+?)\s*\)?$/i);
  if (fn) {
    const repaired = `${fn[1].toLowerCase()}(${fn[2].trim()})`;
    if (FUNC_RE.test(repaired)) return repaired;
  }

  // Bare hex without the leading '#', e.g. "14233F" → "#14233F".
  if (/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(c)) return `#${c}`;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `createTenantTheme: invalid brand color ${JSON.stringify(input)}, using fallback ${fallback}`,
    );
  }
  return fallback;
}

/**
 * Creates a dynamic MUI theme based on tenant branding.
 *
 * Brand colors are sanitized first (see `sanitizeColor`) so malformed tenant
 * data can never crash theme creation. As a final safety net, theme creation is
 * wrapped in try/catch — any unexpected failure falls back to the base theme
 * rather than white-screening the app.
 */
export function createTenantTheme(branding: OrganizationBranding): Theme {
  // Defaults mirror GENERIC_FALLBACK_CONFIG (the Konklave crimson palette).
  const primary = sanitizeColor(branding.primaryColor, '#A6192E');
  const primaryLight = sanitizeColor(branding.primaryLight, '#C8304A');
  const primaryDark = sanitizeColor(branding.primaryDark, '#7A1222');
  const secondary = sanitizeColor(branding.secondaryColor, '#D4A11E');

  try {
    return buildTheme({ primary, primaryLight, primaryDark, secondary });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('createTenantTheme: failed to build tenant theme, using base theme', err);
    return baseTheme;
  }
}

function buildTheme({
  primary,
  primaryLight,
  primaryDark,
  secondary,
}: {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
}): Theme {
  return createTheme({
    palette: {
      primary: {
        main: primary,
        light: primaryLight,
        dark: primaryDark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondary,
        contrastText: '#000000',
      },
      background: {
        default: '#F5F5F5',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#212121',
        secondary: '#616161',
      },
      error: {
        main: '#C62828',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#EF6C00',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#1565C0',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#2E7D32',
        contrastText: '#FFFFFF',
      },
    },
    typography: {
      fontFamily: UI_FONT_STACK,
      // Display sizes are tracked tighter, matching the refreshed designs; the
      // smaller headings keep normal spacing so body-adjacent text stays even.
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.015em' },
      h4: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.015em' },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            '&:focus-visible': {
              outline: `3px solid ${primary}`,
              outlineOffset: '2px',
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
            '&:hover': { boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)' },
            '&:focus-visible': {
              outline: `3px solid ${primary}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `3px solid ${primary}`,
              outlineOffset: '2px',
            },
          },
        },
      },
    },
  });
}
